/**
 * IntegrationLegend — Visual legend for edge types, weights, health.
 */

import React from 'react';
import { RELATIONSHIP_TYPES, HEALTH_COLORS } from './constants';

interface Props {
  nodeCount: number;
  edgeCount: number;
}

const IntegrationLegend: React.FC<Props> = ({ nodeCount, edgeCount }) => {
  return (
    <div
      style={{
        background: 'var(--bg-secondary, rgba(30,41,59,0.8))',
        border: '1px solid var(--border-primary, rgba(255,255,255,0.1))',
        borderRadius: '12px',
        padding: '16px',
        minWidth: '200px',
      }}
    >
      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary, #e2e8f0)', marginBottom: '12px' }}>
        Legend
      </div>

      {/* Edge types */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '11px', color: 'var(--text-secondary, #94a3b8)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Edge Types
        </div>
        {Object.entries(RELATIONSHIP_TYPES).map(([key, meta]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '2px 0' }}>
            <svg width="24" height="4">
              <line
                x1="0" y1="2" x2="24" y2="2"
                stroke={meta.color}
                strokeWidth="2"
                strokeDasharray={meta.dash || undefined}
              />
            </svg>
            <span style={{ fontSize: '12px', color: 'var(--text-primary, #e2e8f0)' }}>{meta.label}</span>
          </div>
        ))}
      </div>

      {/* Weight scale */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '11px', color: 'var(--text-secondary, #94a3b8)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Weight
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <svg width="60" height="8">
            <line x1="0" y1="4" x2="20" y2="4" stroke="#64748b" strokeWidth="1" />
            <line x1="24" y1="4" x2="44" y2="4" stroke="#64748b" strokeWidth="3" />
            <line x1="48" y1="4" x2="60" y2="4" stroke="#64748b" strokeWidth="5" />
          </svg>
          <span style={{ fontSize: '11px', color: 'var(--text-secondary, #94a3b8)' }}>1 &rarr; 10</span>
        </div>
      </div>

      {/* Health */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '11px', color: 'var(--text-secondary, #94a3b8)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Health Status
        </div>
        {Object.entries(HEALTH_COLORS).map(([status, color]) => (
          <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '2px 0' }}>
            <svg width="12" height="12">
              <circle cx="6" cy="6" r="5" fill={color} />
            </svg>
            <span style={{ fontSize: '12px', color: 'var(--text-primary, #e2e8f0)', textTransform: 'capitalize' }}>{status}</span>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div style={{ fontSize: '11px', color: 'var(--text-secondary, #94a3b8)', borderTop: '1px solid var(--border-primary, rgba(255,255,255,0.1))', paddingTop: '8px' }}>
        {nodeCount} nodes &middot; {edgeCount} edges
      </div>
    </div>
  );
};

export default IntegrationLegend;
